/**
 * Slice 50 — first screen of the Clean step: "When you arrive" (screens 04–06, 13).
 * One big check row per task, in the owner's order. A needs-photo task only counts once a photo
 * is attached. "Start the checklist" lights when every required task is ticked.
 */
import { useRef, useState } from "react";
import { Camera, Check } from "lucide-react";
import { cn } from "@/lib/utils";
import { useTranslation } from "@/hooks/useTranslation";
import {
  arrivalDoneCount,
  isArrivalTaskDone,
  requiredTasksLeft,
  type ArrivalAnswer,
  type ArrivalTask,
} from "@/lib/arrivalChecklist";
import { CLEANER_SIMPLE_CTA_FOOTER } from "@/lib/turnoverChrome";

interface Props {
  tasks: ArrivalTask[];
  answers: Record<string, ArrivalAnswer>;
  onToggle: (taskId: string) => void;
  /** Add (or, with replaceUrl, replace) a photo for this task. */
  onAddPhoto: (taskId: string, file: File, replaceUrl?: string) => Promise<void> | void;
  onDeletePhoto: (taskId: string, url: string) => void;
  onStart: () => void;
  busy?: boolean;
}

export function ProgressBars({ total, done }: { total: number; done: number }) {
  return (
    <div className="flex gap-1">
      {Array.from({ length: total }).map((_, i) => (
        <div key={i} className={cn("h-2 flex-1 rounded-full", i < done ? "bg-[#24a89a]" : "bg-[#e1e8ed]")} />
      ))}
    </div>
  );
}

export function TickBox({ done, dashed }: { done: boolean; dashed?: boolean }) {
  if (done) {
    return (
      <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-[#24a89a]">
        <Check className="h-6 w-6 text-white" strokeWidth={3.2} />
      </div>
    );
  }
  return (
    <div
      className={cn(
        "h-11 w-11 shrink-0 rounded-xl",
        dashed ? "border-[2.5px] border-dashed border-[#cdd8df] bg-[#f8fafb]" : "border-[2.5px] border-[#b0bcc4] bg-white",
      )}
    />
  );
}

export function ArrivalChecklistStep({ tasks, answers, onToggle, onAddPhoto, onDeletePhoto, onStart, busy }: Props) {
  const { t, tDynamic } = useTranslation();
  const fileRef = useRef<HTMLInputElement>(null);
  const pending = useRef<{ taskId: string; replaceUrl?: string } | null>(null);
  const [viewing, setViewing] = useState<{ taskId: string; url: string } | null>(null);

  const done = arrivalDoneCount(tasks, answers);
  const left = requiredTasksLeft(tasks, answers);
  const ready = left === 0;

  const openCamera = (taskId: string, replaceUrl?: string) => {
    pending.current = { taskId, replaceUrl };
    fileRef.current?.click();
  };

  return (
    <>
      <input
        ref={fileRef}
        type="file"
        accept="image/*"
        capture="environment"
        className="hidden"
        onChange={(e) => {
          const file = e.target.files?.[0];
          e.target.value = "";
          const target = pending.current;
          pending.current = null;
          if (!file || !target) return;
          setViewing(null);
          void onAddPhoto(target.taskId, file, target.replaceUrl);
        }}
      />
      <div className="flex min-h-0 flex-1 flex-col gap-2.5 overflow-y-auto bg-[#f4f7f9] px-3.5 pb-3 pt-4">
        <div className="flex flex-col gap-2.5 px-1 pb-1">
          <div className="flex items-baseline justify-between gap-2.5">
            <div className="text-[24px] font-bold tracking-[-0.5px] text-[#0f1720]">{t("arrivalTitle")}</div>
            <div className="text-[17px] font-bold text-[#15776d]">
              {t("arrivalDoneOf").replace("{{done}}", String(done)).replace("{{total}}", String(tasks.length))}
            </div>
          </div>
          <ProgressBars total={tasks.length} done={done} />
        </div>

        {tasks.map((task) => {
          const answer = answers[task.id];
          const isDone = isArrivalTaskDone(task, answer);
          const photos = answer?.photo_urls ?? [];
          const tappable = !task.needsPhoto;
          return (
            <div
              key={task.id}
              role={tappable ? "button" : undefined}
              onClick={tappable ? () => onToggle(task.id) : undefined}
              className={cn(
                "flex gap-3.5 rounded-[18px] bg-white p-4",
                task.needsPhoto && photos.length > 0 && isDone
                  ? "border-[2.5px] border-[#24a89a] p-[15px] shadow-[0_6px_18px_rgba(36,168,154,0.18)]"
                  : isDone
                    ? "border-[1.5px] border-[#24a89a]"
                    : "border-[1.5px] border-[#e6edf1]",
                tappable ? "cursor-pointer" : "",
                !task.required && !task.needsPhoto ? "items-center" : "items-start",
              )}
            >
              <TickBox done={isDone} dashed={task.needsPhoto} />
              <div className="flex min-w-0 flex-1 flex-col gap-2">
                {task.required ? (
                  <div className="self-start rounded-full bg-[#eef3f6] px-2.5 py-[3px] text-[12.5px] font-bold text-[#0f1720]">
                    {t("arrivalRequired")}
                  </div>
                ) : null}
                <div className="text-[18.5px] font-bold leading-[1.3] text-[#0f1720] [text-wrap:pretty]">
                  {tDynamic(task.text)}
                </div>
                {task.needsPhoto ? (
                  photos.length === 0 ? (
                    <>
                      <button
                        type="button"
                        onClick={() => openCamera(task.id)}
                        className="flex h-14 items-center justify-center gap-2.5 rounded-[14px] bg-[#e2eeed] text-[18px] font-bold text-[#15776d]"
                      >
                        <Camera className="h-[22px] w-[22px]" strokeWidth={2.2} />
                        {t("arrivalTakePhoto")}
                      </button>
                      <div className="text-[14px] font-medium text-[#64748b]">{t("arrivalTicksItself")}</div>
                    </>
                  ) : (
                    <div className="flex flex-wrap gap-2">
                      {photos.map((url) => (
                        <button
                          key={url}
                          type="button"
                          onClick={() => setViewing({ taskId: task.id, url })}
                          className="h-[76px] w-[76px] overflow-hidden rounded-xl border-[1.5px] border-[#e6edf1]"
                        >
                          <img src={url} alt="" className="h-full w-full object-cover" />
                        </button>
                      ))}
                      <button
                        type="button"
                        onClick={() => openCamera(task.id)}
                        className="flex h-[76px] w-[76px] flex-col items-center justify-center gap-[3px] rounded-xl border-2 border-dashed border-[#9fd3cc] bg-[#f3faf9] text-[#15776d]"
                      >
                        <Camera className="h-5 w-5" strokeWidth={2.2} />
                        <span className="text-[12.5px] font-bold">{t("arrivalAddPhoto")}</span>
                      </button>
                    </div>
                  )
                ) : null}
              </div>
            </div>
          );
        })}
      </div>

      <div className={cn("flex shrink-0 flex-col gap-[9px] border-t border-[#e6edf1] bg-white", CLEANER_SIMPLE_CTA_FOOTER)}>
        <div className={cn("text-center text-[15.5px] font-bold", ready ? "text-[#15776d]" : "text-[#475569]")}>
          {ready
            ? t("arrivalAllDone")
            : left === 1
              ? t("arrivalLeftOne")
              : t("arrivalLeftMany").replace("{{n}}", String(left))}
        </div>
        <button
          type="button"
          disabled={!ready || busy}
          onClick={onStart}
          className={cn(
            "flex h-16 items-center justify-center rounded-[18px] text-[21px] font-bold",
            ready
              ? "bg-[#24a89a] text-white shadow-[0_8px_22px_rgba(36,168,154,0.32)] hover:bg-[#1f958a]"
              : "bg-[#e6edf1] text-[#94a3b0]",
          )}
        >
          {t("arrivalStart")}
        </button>
      </div>

      {viewing ? (
        <div className="fixed inset-0 z-50 flex flex-col bg-black/90">
          <div className="flex min-h-0 flex-1 items-center justify-center p-4">
            <img src={viewing.url} alt="" className="max-h-full max-w-full rounded-xl object-contain" />
          </div>
          <div className="flex gap-3 px-5 pb-[max(1.25rem,env(safe-area-inset-bottom))]">
            <button
              type="button"
              onClick={() => openCamera(viewing.taskId, viewing.url)}
              className="flex h-14 flex-1 items-center justify-center rounded-2xl bg-white text-[17px] font-bold text-[#0f1720]"
            >
              {t("retake")}
            </button>
            <button
              type="button"
              onClick={() => {
                onDeletePhoto(viewing.taskId, viewing.url);
                setViewing(null);
              }}
              className="flex h-14 flex-1 items-center justify-center rounded-2xl bg-white text-[17px] font-bold text-[#b4453a]"
            >
              {t("delete")}
            </button>
            <button
              type="button"
              onClick={() => setViewing(null)}
              className="flex h-14 flex-1 items-center justify-center rounded-2xl bg-[#24a89a] text-[17px] font-bold text-white"
            >
              {t("done")}
            </button>
          </div>
        </div>
      ) : null}
    </>
  );
}
